import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DataService, DialogResult, DialogService, SaveFormEvent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from 'src/app/constants/api-endpoints.constants';
import { OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS, POP_UP_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS } from 'src/app/constants/app-data.constants';
import { TEMPLATES, VERSIONS } from 'src/app/constants/templates.constants';
import { ApplicationDetailCreateUpdatePlacementResponse } from 'src/app/interfaces/application-detail-create-update-placement-response.interface';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { ApplicationRejectPopupQuestion } from '../../interfaces/application-reject-popup-question.interface';
import { ApplicationUserAssignRequestPopupQuestion } from '../../interfaces/application-user-assign-request-popup-question.interface';
import { ApplicationRejectRequest } from '../../models/application-reject-request';
import { ApplicationUserAssignRequest } from '../../models/application-user-assign-request';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-application-detail-create-update-placement',
  templateUrl: './application-detail-create-update-placement.component.html',
  styleUrl: './application-detail-create-update-placement.component.scss'
})
export class ApplicationDetailCreateUpdatePlacementComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getApplicationDetailCreateUpdatePlacementConfigAndResponse;

  templateId: number = TEMPLATES.applicationDetailCreateUpdatePlacement;
  versionNo: number = VERSIONS.latest;
  id: string = '';
  response: ApplicationDetailCreateUpdatePlacementResponse;

  activeDialog: string = '';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {
      home: () => this.home(),
      assign: () => this.assign(),
      accept: () => this.accept(),
      reject: () => this.reject(),
      createClient: () => this.createClient(),
      matchContact: () => this.matchContact(),
      showAssignButton: () => this.showAssignButton(),
      showCreateClientButton: () => this.showCreateClientButton(),
      showMatchContactButton: () => this.showMatchContactButton(),
      showAcceptButton: () => this.showAcceptButton(),
      showRejectButton: () => this.showRejectButton(),
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded($event: any): void {
    this.response = $event as ApplicationDetailCreateUpdatePlacementResponse;
  }

  actionButton_noteListPlaceholder_save_Clicked(event: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.note.save, { parentEntityId: this.id, noteContent: event.field.data,addUserDetails:true })
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        })
    );
  }

  buttonClicked(event: any): void { }
  filter(event: any): void { }
  modelChanged(event: any): void { }
  save(event: SaveFormEvent) { }
  cancel(event: any) {
    this.navigateBackToHome();
  }

  dialogButton_assignApplicationUserPopup_Clicked(event: any): void {
    this.assignApplicationUser(event as ApplicationUserAssignRequestPopupQuestion);
  }

  dialogButton_rejectApplicationPopup_Clicked(event: any): void {
    this.rejectApplication(event as ApplicationRejectPopupQuestion);
  }

  private home(): void {
    this.navigateBackToHome();
  }

  private navigateBackToHome(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    this.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.list], ignoreUnsavedChanges, navExtras);
  }

  private showAssignButton() {
    return this.response.showAssignButton;
  }

  private showCreateClientButton() {
    return this.response.showCreateClientButton;
  }

  private showMatchContactButton() {
    return this.response.showMatchContactButton;
  }

  private showAcceptButton() {
    return this.response.showAcceptButton;
  }

  private showRejectButton() {
    return this.response.showRejectButton;
  }

  private assign() {
    const popupCode = POP_UP_CODES.applicationDetailCreateUpdatePlacement.assignApplicationUser
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode);
  }

  private assignApplicationUser(popupQuestion: ApplicationUserAssignRequestPopupQuestion) {
    const request = new ApplicationUserAssignRequest(this.id, popupQuestion.assignedToUserIdPopup);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.applicationDetailCreateUpdatePlacement.assignApplicationUser, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        }
        )
    );
  }

  private createClient() {
    const popupCode = POP_UP_CODES.applicationDetailCreateUpdatePlacement.confirmCreateClient;

    this.dialogService
      .showConfirm(popupCode)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.completeCreateClient();
            break;
          }
        }
      });
  }

  private completeCreateClient() {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .postAndFetchData(`${API_ENDPOINTS.applicationDetailCreateUpdatePlacement.createClient}/${this.id}`)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        }
        )
    );
  }

  private matchContact() {
    this.router.navigate(['/', ROUTE_CONSTANTS.person.list, ROUTE_CONSTANTS.person.updateCreatePlacementApplicationPrefix, this.id],);
  }

  private accept() {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .postAndFetchData(`${API_ENDPOINTS.applicationDetailCreateUpdatePlacement.acceptApplication}/${this.id}`)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          this.reloadPage(true);
        }
        )
    );
  }

  private reject() {
    const popupCode = POP_UP_CODES.applicationDetailCreateUpdatePlacement.rejectApplication
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode, { applicationRejectionReasonTypeId: REFERENCE_DATA_IDS.applicationStatusReason.rejectedByCahd });
  }

  private rejectApplication(popupQuestion: ApplicationRejectPopupQuestion) {
    const request = new ApplicationRejectRequest(this.id, popupQuestion.applicationRejectionReasonTypeId, popupQuestion.applicationRejectionComments);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.applicationDetailCreateUpdatePlacement.rejectApplication, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.navigateBackToHome();
          }
        }
        )
    );
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        applicationFilterRequest: history.state.applicationFilterRequest
      }
    };
  }
}
